import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import SlideInPanel from '../common/SlideInPanel';
import { workService } from '../../services/workService';
import { WorkStatusBadge } from './WorkStatusBadge';
import { PriorityChip } from './PriorityChip';
import { DueDateLabel } from './DueDateLabel';
import UserBadge from '../common/UserBadge';
import { FiExternalLink } from 'react-icons/fi';

interface Props {
  projectId: string;
  onClose: () => void;
}

export const ProjectDetailPanel: React.FC<Props> = ({ projectId, onClose }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { data: project } = useQuery(['project', projectId], () => workService.getProject(projectId));
  const { data: tasks = [] } = useQuery(['project-tasks-panel', projectId], () => workService.getTasks({ project_id: projectId }));

  if (!project) return null;

  return (
    <SlideInPanel open title={project.title} subtitle={project.description || undefined} accentColor="teal" onClose={onClose}>
      <div className="flex gap-2 mb-4">
        <button onClick={() => { onClose(); navigate(`/projects/${projectId}`); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <FiExternalLink className="w-3.5 h-3.5" /> Open Full Page
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <WorkStatusBadge status={project.status} size="md" />
        <PriorityChip priority={project.priority} />
        <DueDateLabel date={project.due_date} />
        <UserBadge owner={project.owner} size="sm" />
      </div>

      <h3 className={`text-sm font-semibold mt-6 mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Tasks ({tasks.length})
      </h3>
      <div className="space-y-2">
        {tasks.map(t => (
          <div key={t.id}
            onClick={() => { onClose(); navigate(`/tasks/${t.id}`); }}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700/80' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
          >
            <div className="flex items-center justify-between">
              <span className={`font-medium text-sm ${t.status === 'completed' ? 'line-through text-gray-500' : darkMode ? 'text-white' : 'text-gray-900'}`}>{t.title}</span>
              <WorkStatusBadge status={t.status} />
            </div>
            <div className="flex items-center gap-2 mt-1">
              <PriorityChip priority={t.priority} />
              <DueDateLabel date={t.due_date} />
            </div>
          </div>
        ))}
        {tasks.length === 0 && <p className="text-sm text-gray-500">No tasks in this project</p>}
      </div>
    </SlideInPanel>
  );
};
